import { scaleSqrt } from "d3-scale";
import max from "lodash-es/max";
import min from "lodash-es/min";
import sum from "lodash-es/sum";
import { ProductClass, ProductLevel } from "../../graphQL/types/shared";
import { Product } from "../../graphQL/types/shared";
import { generateStringProductId } from "../../graphQL/Utils";
import { formatPercentage, formatTradeValue } from "../../numberFormatters";
import { ITooltipDatum } from "../../tree/hoverTooltips/otherTypes";
import { newHSColorMap } from "../../Utils";
import { newGetDisplayedProductCode } from "../../Utils";
import { IDetailOverlayRow as IRow } from "../../viz/Utils";
import { ExportsChangeNodeSizing } from "../Utils";
import getLinearScaleWithPadding from "./getLinearScaleWithPadding";
import {
  FetchedCountryProductYearData,
  FetchedDatum,
  FetchedProductYearData,
} from "./graphQLTypes";
import { minNodeRadius } from "./nodeFillShadersUtils";
import { ChartNode } from "./otherTypes";
import { sortBy } from "lodash-es";

interface Input {
  fetchedData: FetchedDatum[];
  allProductYearData: FetchedProductYearData[];
  allCountryProductYearData: FetchedCountryProductYearData[];
  width: number;
  height: number;
  codeTooltipLabel: string;
  countryTradeTooltipLabel: string;
  worldTradeTooltipLabel: string;
  growthPercentageLabel: string;
  nodeSizing: ExportsChangeNodeSizing;
  largestContributingExportProduct: Array<{
    id: string;
  }>;
}

type SizeInfo =
  | {
      type: ExportsChangeNodeSizing.CountryTrade;
      quantity: number;
    }
  | {
      type: ExportsChangeNodeSizing.WorldTrade;
      quantity: number;
    }
  | {
      type: ExportsChangeNodeSizing.None;
    };

interface FilteredDatum {
  pci: number;
  id: string;
  growth: number;
  sizeInfo: SizeInfo;
  label: string;
  category: string;
  code: string;
  contributingInfo:
    | { isAmongLargest: false }
    | { isAmongLargest: true; index: number };
}

const maxNodeRadius = 50;

export const getRadiusScale = (
  domain: [number, number],
  range: [number, number]
) => {
  const [domainMin, domainMax] = domain;
  const [rangeMin, rangeMax] = range;
  const radiusScale = scaleSqrt<number, number>()
    .domain([domainMin, domainMax])
    .range([rangeMin, rangeMax]);
  return radiusScale;
};

export default (input: Input) => {
  const {
    fetchedData,
    width,
    height,
    codeTooltipLabel,
    worldTradeTooltipLabel,
    countryTradeTooltipLabel,
    growthPercentageLabel,
    largestContributingExportProduct,
    nodeSizing,
    allProductYearData,
    allCountryProductYearData,
  } = input;
  const filteredByPCI: FilteredDatum[] = [];
  const allGrowths: number[] = [];
  const allPCIs: number[] = [];
  const allSizeNumbers: number[] = [];
  for (const fetchedDatum of fetchedData) {
    const {
      product: {
        id,
        shortName,
        code,
        topLevelParent: { id: category },
      },
      exportGrowthCAGR,
    } = fetchedDatum;

    const productYearDatum = allProductYearData.find(
      (d) => d.product.id === id
    );
    const pci =
      productYearDatum && productYearDatum.pci !== null
        ? productYearDatum.pci
        : null;
    const globalExportValue =
      productYearDatum && productYearDatum.globalExportValue !== null
        ? productYearDatum.globalExportValue
        : null;

    const countryProductYearDatum = allCountryProductYearData.find(
      (d) => d.product.id === id
    );
    const exportValue =
      countryProductYearDatum && countryProductYearDatum.exportValue !== null
        ? countryProductYearDatum.exportValue
        : null;

    const otherCategoryID = generateStringProductId({
      productClass: ProductClass.HS,
      id: 10,
    });
    const largestContributorId = largestContributingExportProduct[0].id;
    const secondLargestContributorId = largestContributingExportProduct[1].id;
    const thirdLargestContributorId = largestContributingExportProduct[2].id;

    if (
      pci !== null &&
      exportGrowthCAGR !== null &&
      category !== otherCategoryID
    ) {
      let contributingInfo: FilteredDatum["contributingInfo"];
      if (id === largestContributorId) {
        contributingInfo = { isAmongLargest: true, index: 0 };
      } else if (id === secondLargestContributorId) {
        contributingInfo = { isAmongLargest: true, index: 1 };
      } else if (id === thirdLargestContributorId) {
        contributingInfo = { isAmongLargest: true, index: 2 };
      } else {
        contributingInfo = { isAmongLargest: false };
      }
      let sizeInfo: SizeInfo | undefined;
      if (
        nodeSizing === ExportsChangeNodeSizing.CountryTrade &&
        exportValue !== null
      ) {
        sizeInfo = {
          type: ExportsChangeNodeSizing.CountryTrade,
          quantity: exportValue,
        };
        allSizeNumbers.push(exportValue);
      } else if (
        nodeSizing === ExportsChangeNodeSizing.WorldTrade &&
        globalExportValue !== null
      ) {
        sizeInfo = {
          type: ExportsChangeNodeSizing.WorldTrade,
          quantity: globalExportValue,
        };
        allSizeNumbers.push(globalExportValue);
      } else if (nodeSizing === ExportsChangeNodeSizing.None) {
        sizeInfo = { type: ExportsChangeNodeSizing.None };
      }
      if (sizeInfo !== undefined) {
        const next: FilteredDatum = {
          pci,
          id,
          growth: exportGrowthCAGR,
          sizeInfo,
          label: shortName,
          category,
          code,
          contributingInfo,
        };
        allGrowths.push(exportGrowthCAGR);
        allPCIs.push(pci);
        filteredByPCI.push(next);
      }
    }
  }

  const originalMinGrowth = min(allGrowths);
  const originalMaxGrowth = max(allGrowths);
  const originalMinPCI = min(allPCIs);
  const originalMaxPCI = max(allPCIs);

  if (
    originalMinGrowth === undefined ||
    originalMaxGrowth === undefined ||
    originalMinPCI === undefined ||
    originalMaxPCI === undefined
  ) {
    throw new Error(
      "One of these extremums are undefined, possibly due to empty list"
    );
  }
  const {
    scale: yScale,
    domainStart: minGrowth,
    domainEnd: maxGrowth,
  } = getLinearScaleWithPadding(
    [originalMinGrowth, originalMaxGrowth],
    [height, 0],
    maxNodeRadius
  );
  const {
    scale: xScale,
    domainStart: minPCI,
    domainEnd: maxPCI,
  } = getLinearScaleWithPadding(
    [originalMinPCI, originalMaxPCI],
    [0, width],
    maxNodeRadius
  );

  let nodes: ChartNode[], tooltipMapPrecursors: Array<[string, ITooltipDatum]>;
  if (
    nodeSizing === ExportsChangeNodeSizing.CountryTrade ||
    nodeSizing === ExportsChangeNodeSizing.WorldTrade
  ) {
    const maxSize = max(allSizeNumbers);
    const minSize = min(allSizeNumbers);
    if (maxSize === undefined || minSize === undefined) {
      throw new Error(
        "One of these extremums are undefined, possibly due to empty list"
      );
    }
    const radiusScale = getRadiusScale(
      [minSize, maxSize],
      [minNodeRadius, maxNodeRadius]
    );
    const unsortedNodes = filteredByPCI.map(
      ({ pci, id, growth, sizeInfo, label, category, contributingInfo }) => {
        const retrievedColor = newHSColorMap.get(category);
        if (retrievedColor === undefined) {
          throw new Error("Cannot find color for category " + category);
        }
        let radiusScaleInput: number;
        if (
          sizeInfo.type === ExportsChangeNodeSizing.CountryTrade ||
          sizeInfo.type === ExportsChangeNodeSizing.WorldTrade
        ) {
          radiusScaleInput = sizeInfo.quantity;
        } else {
          throw new Error("This code path should not happen");
        }
        const out: ChartNode = {
          id,
          x: xScale(pci),
          y: yScale(growth),
          radius: radiusScale(radiusScaleInput),
          label,
          color: retrievedColor,
          contributingInfo,
        };
        return out;
      }
    );
    nodes = unsortedNodes.sort((a, b) => b.radius - a.radius);
    tooltipMapPrecursors = filteredByPCI.map((datum) => {
      const { label, id, pci, sizeInfo, growth, code } = datum;
      let sizeNumber: number, tradeLabel: string;
      if (sizeInfo.type === ExportsChangeNodeSizing.CountryTrade) {
        sizeNumber = sizeInfo.quantity;
        tradeLabel = countryTradeTooltipLabel;
      } else if (sizeInfo.type === ExportsChangeNodeSizing.WorldTrade) {
        sizeNumber = sizeInfo.quantity;
        tradeLabel = worldTradeTooltipLabel;
      } else {
        throw new Error("This code path should not happen");
      }
      const radius = radiusScale(sizeNumber);
      const codeInfo: IRow = {
        label: codeTooltipLabel,
        value: newGetDisplayedProductCode(
          code,
          ProductClass.HS,
          ProductLevel.fourDigit
        ),
      };
      const tradeInfo: IRow = {
        label: tradeLabel,
        value: formatTradeValue(sizeNumber),
      };
      const growthPercentageInfo: IRow = {
        label: growthPercentageLabel,
        value: formatPercentage(growth),
      };
      const outDatum: ITooltipDatum = {
        id,
        title: label,
        tooltipX: xScale(pci),
        tooltipY: yScale(growth) - radius,
        rows: [codeInfo, tradeInfo, growthPercentageInfo],
      };
      return [id, outDatum] as [string, ITooltipDatum];
    });
  } else {
    const radius = minNodeRadius + (maxNodeRadius - minNodeRadius) / 4;
    nodes = filteredByPCI.map(
      ({ pci, id, growth, label, category, contributingInfo }) => {
        const retrievedColor = newHSColorMap.get(category);
        if (retrievedColor === undefined) {
          throw new Error("Cannot find color for category " + category);
        }
        const out: ChartNode = {
          id,
          x: xScale(pci),
          y: yScale(growth),
          radius,
          label,
          color: retrievedColor,
          contributingInfo,
        };
        return out;
      }
    );
    tooltipMapPrecursors = filteredByPCI.map((datum) => {
      const { label, id, pci, growth, code } = datum;
      const codeInfo: IRow = {
        label: codeTooltipLabel,
        value: newGetDisplayedProductCode(
          code,
          ProductClass.HS,
          ProductLevel.fourDigit
        ),
      };
      const growthPercentageInfo: IRow = {
        label: growthPercentageLabel,
        value: formatPercentage(growth),
      };
      const outDatum: ITooltipDatum = {
        id,
        title: label,
        tooltipX: xScale(pci),
        tooltipY: yScale(growth) - radius,
        rows: [codeInfo, growthPercentageInfo],
      };
      return [id, outDatum] as [string, ITooltipDatum];
    });
  }

  const averagePCI = sum(allPCIs) / allPCIs.length;
  return {
    nodes,
    minGrowth,
    maxGrowth,
    minPCI,
    maxPCI,
    averagePCI,
    tooltipMap: new Map(tooltipMapPrecursors),
  };
};
